import {useEffect, useMemo, useState} from "react";
import {
    Paper, Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
    Typography, Chip, Box, useTheme, TablePagination,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, List, ListItem
} from "@mui/material";
import {HelpOutline} from "@mui/icons-material";
import {formatDate} from "../utils/formatters";
import {SegmentChip, SEGMENT_COLORS} from "./SegmentChip";

// Matches the "Watch" / "Lost" segment chip colors exactly, per request.
// Labeled "Stopped Buying" (not "Lost") so it doesn't collide with the RFM
// Segment column's own "Lost" chip - same underlying tier, different word.
const RISK_TIER_COLORS = {
    'Stopped Buying':   {bg: '#9E9E9E', fg: '#fff'},
    'At Risk':          {bg: '#F44336', fg: '#fff'},
    'Warning':          {bg: '#FB8C00', fg: '#fff'},
    'Watch':            {bg: 'rgb(187, 214, 238)', fg: '#0d47a1'},
    'Healthy':          {bg: '#43A047', fg: '#fff'},
    'Insufficient Data': {bg: '#CFD8DC', fg: '#455A64'},
};

const RiskTierChip = ({label}) => {
    if (!label) return null;
    const colors = RISK_TIER_COLORS[label] || {bg: '#607D8B', fg: '#fff'};
    return <Chip size="small" label={label} style={{background: colors.bg, color: colors.fg, fontWeight: 600}}/>;
};

const NoSegmentChip = () => (
    <Chip size="small" label="No Segment" variant="outlined" style={{color: '#9e9e9e', borderColor: '#9e9e9e'}}/>
);

// ---- Severity coloring (Total Risk Score, each signal Score, Overdue Ratio,
// Delta/Delta %) - one shared 5-step scale, reused by the band tables in the
// help modals so the colors mean the same thing everywhere on this page. ----
const SEVERITY_COLORS = {
    healthy: '#2E7D32', low: '#7CB342', medium: '#F9A825', high: '#FB8C00', critical: '#E53935',
};
const SEVERITY_LABELS = {
    healthy: 'Healthy', low: 'Low risk', medium: 'Medium risk', high: 'High risk', critical: 'Critical risk',
};
// Recency, Number of Orders, Net Revenue, and VM% all score on the same
// 0/1/2/5/10-point scale. AOV and Product Mix are secondary (half-weighted)
// signals on their own 0/1/1.5/3/5-point scale - same band names, different points.
const PRIMARY_POINT_SEVERITY = {0: 'healthy', 1: 'low', 2: 'medium', 5: 'high', 10: 'critical'};
const SECONDARY_POINT_SEVERITY = {0: 'healthy', 1: 'low', 1.5: 'medium', 3: 'high', 5: 'critical'};
const severityColor = (points, severityMap) => {
    if (points == null || !severityMap) return undefined;
    const key = severityMap[points];
    return key ? SEVERITY_COLORS[key] : undefined;
};

// Plain up/down coloring for Delta and Delta % (and Overdue Ratio's own
// simpler good/bad split below) - separate from the 5-step severity scale
// used for Score, which stays as-is.
const POSITIVE_COLOR = SEVERITY_COLORS.healthy;
const NEGATIVE_COLOR = SEVERITY_COLORS.critical;
const trendColor = (v) => {
    if (v == null || v === 0) return undefined;
    return v > 0 ? POSITIVE_COLOR : NEGATIVE_COLOR;
};

const RECENCY_RATIO_BANDS = [
    {range: '< 1.5x overdue', severity: 'healthy', points: '0 pts'},
    {range: '1.5x – 4x', severity: 'low', points: '1 pt'},
    {range: '4x – 6x', severity: 'medium', points: '2 pts'},
    {range: '6x – 8x', severity: 'high', points: '5 pts'},
    {range: '8x – 10x', severity: 'critical', points: '10 pts'},
];
const RECENCY_FALLBACK_BANDS = [
    {range: '< 180 days', severity: 'healthy', points: '0 pts'},
    {range: '180 – 365 days', severity: 'medium', points: '2 pts (flat)'},
    {range: '365+ days', severity: 'critical', points: '"Stopped Buying"'},
];
const PRIMARY_DECLINE_BANDS = [
    {range: '< 25% decline', severity: 'healthy', points: '0 pts'},
    {range: '25% – 40%', severity: 'low', points: '1 pt'},
    {range: '40% – 60%', severity: 'medium', points: '2 pts'},
    {range: '60% – 80%', severity: 'high', points: '5 pts'},
    {range: '80%+', severity: 'critical', points: '10 pts'},
];
const SECONDARY_DECLINE_BANDS = [
    {range: '< 25% decline', severity: 'healthy', points: '0 pts'},
    {range: '25% – 40%', severity: 'low', points: '1 pt'},
    {range: '40% – 60%', severity: 'medium', points: '1.5 pts'},
    {range: '60% – 80%', severity: 'high', points: '3 pts'},
    {range: '80%+', severity: 'critical', points: '5 pts'},
];
const MARGIN_POINT_DROP_BANDS = [
    {range: '< 3 pts lost', severity: 'healthy', points: '0 pts'},
    {range: '3 – 6 pts', severity: 'low', points: '1 pt'},
    {range: '6 – 10 pts', severity: 'medium', points: '2 pts'},
    {range: '10 – 15 pts', severity: 'high', points: '5 pts'},
    {range: '15 pts+', severity: 'critical', points: '10 pts'},
];
const TOTAL_SCORE_BANDS = [
    {range: '0 points', severity: 'healthy', points: 'Healthy'},
    {range: '1 – 4 points', severity: 'low', points: 'Watch'},
    {range: '5 – 9 points', severity: 'medium', points: 'Warning'},
    {range: '10+ points', severity: 'critical', points: 'At Risk'},
];

// ---- Local formatters - signal values are already percent-scaled numbers
// (25.3 meaning 25.3%), unlike utils/formatters.js's fraction-based formatPercent. ----
const money = (v) => {
    if (v == null) return '—';
    return v.toLocaleString('en-US', {style: 'currency', currency: 'USD', maximumFractionDigits: 0});
};
const signedMoney = (v) => {
    if (v == null) return '—';
    if (v === 0) return '$0';
    return `${v > 0 ? '+' : '-'}${money(Math.abs(v))}`;
};
const count = (v) => (v == null ? '—' : Math.round(v).toLocaleString());
const signedCount = (v) => {
    if (v == null) return '—';
    if (v === 0) return '0';
    return `${v > 0 ? '+' : '-'}${Math.round(Math.abs(v)).toLocaleString()}`;
};
const pct1 = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
const signedPct1 = (v) => {
    if (v == null) return '—';
    if (v === 0) return '0%';
    return `${v > 0 ? '+' : '-'}${Math.abs(v).toFixed(1)}%`;
};
const ratioX = (v) => (v == null ? '—' : `${v.toFixed(1)}×`);
const scoreVal = (v) => (v == null ? '—' : v);

const Lines = ({children}) => (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.25}}>{children}</Box>
);
const PrimaryLine = ({children}) => <Typography variant="body2">{children}</Typography>;
const SecondaryLine = ({children}) => (
    <Typography variant="caption" color="textSecondary">{children}</Typography>
);
/** The Score sub-value, bold + severity-colored (the one number in each signal that's meant to stand out). */
const ScoreVal = ({points, severityMap}) => (
    <span style={{color: severityColor(points, severityMap), fontWeight: 800}}>{scoreVal(points)}</span>
);

/** Renders a severity-banded rule (e.g. "1.5x–4x overdue -> Low risk, 1 pt") as a small colored legend. */
const BandsTable = ({bands}) => (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5, mb: 0.5}}>
        {bands.map((b, i) => (
            <Box key={i} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                <Box sx={{width: 10, height: 10, borderRadius: '50%', background: SEVERITY_COLORS[b.severity], flexShrink: 0}}/>
                <Typography variant="body2" sx={{minWidth: 130}}>{b.range}</Typography>
                <Typography variant="body2" sx={{flexGrow: 1, fontWeight: 600, color: SEVERITY_COLORS[b.severity]}}>
                    {SEVERITY_LABELS[b.severity] || b.severity}
                </Typography>
                <Typography variant="body2" color="textSecondary">{b.points}</Typography>
            </Box>
        ))}
    </Box>
);

// The same 3x3 Recency x Frequency grid as the RfmSegmentGrid component on
// the page, without live counts - a legend explaining where each segment
// name comes from. Row order top-to-bottom = high -> low frequency; column
// order left-to-right = high -> low recency (matches RfmSegmentGrid.js).
const RFM_GRID_ROWS = [
    ['Champions', 'Loyal', 'At Risk'],
    ['Potential Loyalists', 'Need Attention', 'Hibernating'],
    ['New Customers', 'Promising', 'Lost'],
];
const RfmGridLegend = () => (
    <Box sx={{display: 'flex', mt: 1, mb: 2}}>
        <Box sx={{
            writingMode: 'vertical-rl', transform: 'rotate(180deg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 11, color: 'text.secondary', mr: 1, flexShrink: 0,
        }}>
            Frequency (low → high)
        </Box>
        <Box sx={{flex: 1}}>
            <Box sx={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75}}>
                {RFM_GRID_ROWS.flat().map((segment) => {
                    const colors = SEGMENT_COLORS[segment] || {bg: '#607D8B', fg: '#fff'};
                    return (
                        <Box
                            key={segment}
                            sx={{
                                background: colors.bg, color: colors.fg, borderRadius: 1,
                                p: 1, textAlign: 'center', fontSize: 12, fontWeight: 600,
                            }}
                        >
                            {segment}
                        </Box>
                    );
                })}
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{display: 'block', textAlign: 'center', mt: 0.5}}>
                Recency (high → low)
            </Typography>
        </Box>
    </Box>
);

// ---- Column help (the "?" button next to every header except Account) ----
const ColumnHelpModal = ({help, open, onClose}) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{help.title}</DialogTitle>
        <DialogContent>
            {help.intro.map((p, i) => (
                <Typography key={i} variant="body2" color="textSecondary" sx={{mb: 1.5}}>{p}</Typography>
            ))}
            {help.visual}
            <List dense disablePadding>
                {help.items.map((item, i) => (
                    <ListItem key={i} disableGutters sx={{display: 'block', pb: 1.25}}>
                        <Typography variant="subtitle2">{item.label}</Typography>
                        {item.description && (
                            <Typography variant="body2" color="textSecondary">{item.description}</Typography>
                        )}
                        {item.bands && <BandsTable bands={item.bands}/>}
                    </ListItem>
                ))}
            </List>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Close</Button>
        </DialogActions>
    </Dialog>
);

const ColumnHelpButton = ({help}) => {
    const [open, setOpen] = useState(false);
    if (!help) return null;
    return (
        <>
            <IconButton
                size="small"
                aria-label={`About ${help.title}`}
                onClick={(e) => { e.stopPropagation(); setOpen(true); }}
                sx={{p: 0.25}}
            >
                <HelpOutline sx={{fontSize: 16}}/>
            </IconButton>
            <ColumnHelpModal help={help} open={open} onClose={() => setOpen(false)}/>
        </>
    );
};

/** Shared Current/Prior/Delta/Delta %/Score explanation for a buildSignalColumn column. */
const signalHelp = (title, {about, bands}) => ({
    title,
    intro: [about],
    items: [
        {label: 'Current', description: 'Trailing 90 days, ending on the most recent order date in the system.'},
        {label: 'Prior', description: 'The same 90-day window, one year earlier.'},
        {
            label: 'Delta / Delta %',
            description: 'Current minus Prior, and that change as a percentage. Shows "—" for Delta % when Prior was 0 - there is no baseline to compute a percent change against.',
        },
        ...(bands ? [{
            label: 'Score',
            description: 'Points scale with how much it has declined vs. the same window last year:',
            bands,
        }] : [{
            label: 'No Score',
            description: 'This signal has no dedicated point-scoring rule of its own - margin risk is captured by the VM% column instead, to avoid scoring the same decline twice.',
        }]),
    ],
});

/**
 * Builds the Current/Prior/Delta/Delta %[/Score] sub-list + cell for one
 * $-or-count signal column. Pass showScore: false for a signal with no
 * dedicated point-scoring rule (e.g. VM $) so no "Score" sub/value appears,
 * and severityMap decides what color (if any) the Delta/Delta %/Score line gets.
 */
const buildSignalColumn = (key, main, {valueFmt, deltaFmt, showScore = true, severityMap, help}) => ({
    key,
    main,
    align: 'right',
    help: help ? signalHelp(main, help) : undefined,
    subs: [
        {id: 'current', path: `${key}.current`, label: 'Current'},
        {id: 'prior', path: `${key}.prior`, label: 'Prior'},
        {id: 'delta', path: `${key}.delta`, label: 'Delta'},
        {id: 'delta_pct', path: `${key}.delta_pct`, label: 'Delta %'},
        ...(showScore ? [{id: 'points', path: `${key}.points`, label: 'Score'}] : []),
    ],
    headerLines: showScore ? [['current'], ['prior'], ['delta', 'delta_pct', 'points']] : [['current'], ['prior'], ['delta', 'delta_pct']],
    renderCell: (row) => {
        const s = row[key];
        return (
            <Lines>
                <PrimaryLine>{valueFmt(s.current)}</PrimaryLine>
                <SecondaryLine>{valueFmt(s.prior)}</SecondaryLine>
                <SecondaryLine>
                    <span style={{color: trendColor(s.delta)}}>{deltaFmt(s.delta)}</span>
                    {' / '}
                    <span style={{color: trendColor(s.delta_pct)}}>{signedPct1(s.delta_pct)}</span>
                    {showScore && <>{' / '}<ScoreVal points={s.points} severityMap={severityMap}/></>}
                </SecondaryLine>
            </Lines>
        );
    },
});

const COLUMNS = [
    {
        key: 'account',
        main: 'Account',
        align: 'left',
        width: 300,
        subs: [
            {id: 'name', path: 'name', label: 'Account Name'},
            {id: 'number', path: 'number', label: 'Account #'},
            {id: 'parent', path: 'parent_account_name', label: 'Parent Account'},
        ],
        headerLines: [['name'], ['number'], ['parent']],
        renderCell: (row) => (
            <Lines>
                <PrimaryLine><strong>{row.name}</strong></PrimaryLine>
                <SecondaryLine>{row.number || '—'}</SecondaryLine>
                <SecondaryLine>{row.parent_account_name || '—'}</SecondaryLine>
            </Lines>
        ),
    },
    {
        key: 'segment',
        main: 'Segment',
        align: 'left',
        path: 'segment',
        renderCell: (row) => (row.segment ? <SegmentChip segment={row.segment}/> : <NoSegmentChip/>),
        help: {
            title: 'Segment (RFM)',
            intro: [
                'Segment comes from the RFM model on the Customer Churn page - the same ML-driven segmentation, shown here for context alongside the live-order-history Risk Score.',
                'Every account gets a Recency tier (how recently they last ordered) and a Frequency tier (how often they order), each bucketed into High / Medium / Low. Those two tiers together place the account into one of 9 segments, laid out the same way as the matrix at the top of this page:',
                'Monetary (total spend) is tracked by the same model but isn’t part of which segment an account lands in - only Recency and Frequency are.',
            ],
            visual: <RfmGridLegend/>,
            items: [
                {label: 'Champions', description: 'High recency + high frequency - order most recently AND most often. Protect: they anchor the revenue base and are the most expensive to lose.'},
                {label: 'Loyal', description: 'Medium recency + high frequency - still order fairly often, just a notch below Champions. Protect: a stable base of repeat buyers.'},
                {label: 'At Risk', description: 'Low recency + high frequency - used to order often, but it has been a while. Repair: a previously valuable relationship going quiet.'},
                {label: 'Potential Loyalists', description: 'High recency + medium frequency - ordered recently, at a moderate pace. Invest: engaged, not yet fully locked in.'},
                {label: 'Need Attention', description: 'Medium recency + medium frequency - a warning sign, not yet a crisis. Repair: intervene before it slides further.'},
                {label: 'Hibernating', description: 'Low recency + medium frequency - a long gap since the last order and a moderate history. Largely disengaged already.'},
                {label: 'New Customers', description: 'High recency + low frequency - a very recent first buyer with minimal order history. Too early to judge either way.'},
                {label: 'Promising', description: 'Medium recency + low frequency - an early-stage relationship showing some traction.'},
                {label: 'Lost', description: 'Low recency + low frequency - the longest gap and the lowest order count. Effectively inactive.'},
                {label: 'No Segment', description: 'This account has no RFM snapshot from the Customer Churn model yet (a data gap, not a real segment) - a small share of ship-to accounts are currently missing one.'},
            ],
        },
    },
    {
        key: 'risk_score',
        main: 'Risk Score',
        align: 'left',
        path: 'total_risk_score',
        renderCell: (row) => (
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1}}>
                <PrimaryLine><strong>{scoreVal(row.total_risk_score)}</strong></PrimaryLine>
                <RiskTierChip label={row.risk_tier_label}/>
            </Box>
        ),
        help: {
            title: 'Risk Score',
            intro: [
                'Risk Score is independent of the Segment column - it is calculated live from order history across 6 signals: Recency, Number of Orders, Net Revenue, VM%, AOV, and Product Mix.',
                'Each signal contributes 0 or more points based on how far it has declined vs. the same 90-day window last year. Total Risk Score is the sum of all 6, and sets the tier chip below it:',
            ],
            items: [
                {label: 'Tier', bands: TOTAL_SCORE_BANDS},
                {
                    label: 'Stopped Buying',
                    description: 'A separate, already-confirmed state, not a point-scored prediction - shown instead of a tier above. The account has gone far beyond its normal order cadence (10x+ its typical gap between orders), or - when there is too little order history to establish a cadence - it has been 365+ days since its only/last order. (Labeled "Stopped Buying" rather than "Lost" so it isn’t confused with the RFM "Lost" segment above.)',
                },
                {
                    label: 'Insufficient Data',
                    description: 'Also shown instead of a tier above. None of the 6 signals could be measured (too little order history to compare against a prior-year period) and recency didn’t flag anything either - there isn’t enough evidence to call this account healthy or at risk.',
                },
            ],
        },
    },
    {
        key: 'recency',
        main: 'Recency',
        align: 'right',
        subs: [
            {id: 'date', path: 'recency.date_last_order', label: 'Date Last Order'},
            {id: 'overdue_days', path: 'recency.overdue_days', label: 'Overdue Days'},
            {id: 'typical_gap', path: 'recency.typical_gap_days', label: 'Typical Gap'},
            {id: 'ratio', path: 'recency.lateness_ratio', label: 'Overdue Ratio'},
            {id: 'points', path: 'recency.points', label: 'Score'},
        ],
        headerLines: [['date'], ['overdue_days', 'typical_gap'], ['ratio', 'points']],
        renderCell: (row) => {
            const ratio = row.recency.lateness_ratio;
            const ratioColor = ratio == null ? undefined : (ratio < 1.5 ? POSITIVE_COLOR : NEGATIVE_COLOR);
            return (
                <Lines>
                    <PrimaryLine>{formatDate(row.recency.date_last_order) || '—'}</PrimaryLine>
                    <SecondaryLine>
                        {count(row.recency.overdue_days)} days ago / {
                            row.recency.typical_gap_days == null ? '—' : `${count(row.recency.typical_gap_days)}d typical`
                        }
                    </SecondaryLine>
                    <SecondaryLine>
                        <span style={{color: ratioColor}}>{ratioX(ratio)}</span>
                        {' / '}
                        <ScoreVal points={row.recency.points} severityMap={PRIMARY_POINT_SEVERITY}/>
                    </SecondaryLine>
                </Lines>
            );
        },
        help: {
            title: 'Recency',
            intro: [
                'How overdue this account is for its next order, based on its own historical ordering cadence.',
                'When there’s no Typical Gap on record (fewer than 3 orders ever), Overdue Ratio and Score both show "—" here - a different rule applies instead, based only on Overdue Days. See the last item below.',
            ],
            items: [
                {label: 'Date Last Order', description: 'The date of this account’s most recent order.'},
                {label: 'Overdue Days', description: 'Days between that order and the most recent order date anywhere in the system (not necessarily today - order data refreshes periodically).'},
                {label: 'Typical Gap', description: 'The account’s own average number of days between orders, historically. Shows "—" when there isn’t enough order history (fewer than 3 orders ever) to establish one - see the fallback rule below.'},
                {label: 'Overdue Ratio', description: 'Overdue Days divided by Typical Gap. 2.0x means it has been twice as long as usual since they last ordered. Shows "—" whenever Typical Gap does, for the same reason.'},
                {label: 'Score', description: 'Points scale with how overdue the account is, based on the Overdue Ratio above (capped below 10x, which is "Stopped Buying" instead):', bands: RECENCY_RATIO_BANDS},
                {label: 'The fallback rule: when there’s no Typical Gap to compare against (fewer than 3 orders ever)', description: 'Overdue Ratio and Score are replaced entirely by a simpler rule based only on Overdue Days:', bands: RECENCY_FALLBACK_BANDS},
            ],
        },
    },
    buildSignalColumn('orders', 'Number of Orders', {
        valueFmt: count, deltaFmt: signedCount, severityMap: PRIMARY_POINT_SEVERITY,
        help: {about: 'Number of distinct orders placed.', bands: PRIMARY_DECLINE_BANDS},
    }),
    buildSignalColumn('net_revenue', 'Net Revenue', {
        valueFmt: money, deltaFmt: signedMoney, severityMap: PRIMARY_POINT_SEVERITY,
        help: {about: 'Total net revenue across all orders.', bands: PRIMARY_DECLINE_BANDS},
    }),
    buildSignalColumn('variable_margin', 'VM', {
        valueFmt: money, deltaFmt: signedMoney, showScore: false,
        help: {about: 'Variable margin dollars (net revenue minus variable cost of goods sold).'},
    }),
    {
        key: 'variable_margin_pct',
        main: 'VM%',
        align: 'right',
        subs: [
            {id: 'current', path: 'variable_margin_pct.current', label: 'Current'},
            {id: 'prior', path: 'variable_margin_pct.prior', label: 'Prior'},
            {id: 'delta_points', path: 'variable_margin_pct.delta_points', label: 'Delta'},
            {id: 'points', path: 'variable_margin_pct.points', label: 'Score'},
        ],
        headerLines: [['current'], ['prior'], ['delta_points', 'points']],
        renderCell: (row) => {
            const s = row.variable_margin_pct;
            return (
                <Lines>
                    <PrimaryLine>{pct1(s.current)}</PrimaryLine>
                    <SecondaryLine>{pct1(s.prior)}</SecondaryLine>
                    <SecondaryLine>
                        <span style={{color: trendColor(s.delta_points)}}>{signedPct1(s.delta_points)}</span>
                        {' / '}
                        <ScoreVal points={s.points} severityMap={PRIMARY_POINT_SEVERITY}/>
                    </SecondaryLine>
                </Lines>
            );
        },
        help: {
            title: 'VM%',
            intro: ['Variable margin as a percentage of net revenue - profitability per order, independent of revenue size.'],
            items: [
                {label: 'Current', description: 'Trailing 90 days, ending on the most recent order date in the system.'},
                {label: 'Prior', description: 'The same 90-day window, one year earlier.'},
                {label: 'Delta', description: 'Current minus Prior (e.g. 45% → 40% shows as -5%). This is the point difference between the two percentages, not a relative percent change like the Delta % columns elsewhere.'},
                {label: 'Score', description: 'Points scale with how many margin points it has dropped vs. the same window last year:', bands: MARGIN_POINT_DROP_BANDS},
            ],
        },
    },
    buildSignalColumn('aov', 'AOV', {
        valueFmt: money, deltaFmt: signedMoney, severityMap: SECONDARY_POINT_SEVERITY,
        help: {about: 'Average order value (net revenue ÷ number of orders). A secondary, lower-weighted signal.', bands: SECONDARY_DECLINE_BANDS},
    }),
    buildSignalColumn('product_mix', 'Product Mix', {
        valueFmt: count, deltaFmt: signedCount, severityMap: SECONDARY_POINT_SEVERITY,
        help: {about: 'Number of distinct products (SKUs) ordered. A secondary, lower-weighted signal.', bands: SECONDARY_DECLINE_BANDS},
    }),
];

const getByPath = (row, path) => path.split('.').reduce((acc, key) => (acc == null ? acc : acc[key]), row);

// Nulls (Stopped Buying / Insufficient Data have no computed total_risk_score,
// missing dates, etc.) always rank as the lowest value - so they sink to the
// bottom under the default "highest risk first" sort instead of floating to
// the top, regardless of which direction the column is currently sorted.
const compareValues = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return -1;
    if (b == null) return 1;
    if (typeof a === 'string' || typeof b === 'string') return String(a).localeCompare(String(b));
    return a - b;
};

const sortRows = (rows, sortKey, sortDir) => {
    if (!sortKey) return rows;
    const direction = sortDir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
        const cmp = compareValues(getByPath(a, sortKey), getByPath(b, sortKey));
        if (cmp !== 0) return cmp * direction;
        return String(a.name || '').localeCompare(String(b.name || '')) * direction;
    });
};

/** One clickable sub-label, used standalone or grouped (joined with " / ") in a header line. */
const SortableSub = ({sub, sortKey, sortDir, onSort}) => {
    const active = sortKey === sub.path;
    return (
        <Typography
            component="span"
            variant="caption"
            color={active ? 'primary' : 'textSecondary'}
            onClick={() => onSort(sub.path)}
            style={{cursor: 'pointer', fontWeight: active ? 700 : 400, whiteSpace: 'nowrap'}}
        >
            {sub.label}{active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
        </Typography>
    );
};

const ColumnHeaderCell = ({column, sortKey, sortDir, onSort}) => {
    if (!column.subs) {
        const active = sortKey === column.path;
        return (
            <TableCell align={column.align} style={{fontWeight: 600, verticalAlign: 'top', width: column.width}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.25}}>
                    <Typography
                        component="span"
                        variant="body2"
                        color={active ? 'primary' : 'inherit'}
                        onClick={() => onSort(column.path)}
                        style={{cursor: 'pointer', fontWeight: 600}}
                    >
                        {column.main}
                    </Typography>
                    <ColumnHelpButton help={column.help}/>
                    {active && (
                        <Typography
                            component="span"
                            variant="body2"
                            color="primary"
                            onClick={() => onSort(column.path)}
                            style={{cursor: 'pointer'}}
                        >
                            {sortDir === 'asc' ? '↑' : '↓'}
                        </Typography>
                    )}
                </Box>
            </TableCell>
        );
    }

    const alignItems = column.align === 'right' ? 'flex-end' : 'flex-start';
    const subById = Object.fromEntries(column.subs.map((sub) => [sub.id, sub]));

    return (
        <TableCell align={column.align} style={{fontWeight: 600, verticalAlign: 'top', width: column.width}}>
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems, gap: 0.25}}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0}}>
                    <Typography variant="body2" style={{fontWeight: 700}}>{column.main}</Typography>
                    <ColumnHelpButton help={column.help}/>
                </Box>
                {column.headerLines.map((group, i) => (
                    <Box key={i} sx={{display: 'flex', gap: 0.5}}>
                        {group.map((subId, j) => (
                            <span key={subId} style={{display: 'flex', alignItems: 'center', gap: 4}}>
                                <SortableSub sub={subById[subId]} sortKey={sortKey} sortDir={sortDir} onSort={onSort}/>
                                {j < group.length - 1 && <Typography variant="caption" color="textSecondary">/</Typography>}
                            </span>
                        ))}
                    </Box>
                ))}
            </Box>
        </TableCell>
    );
};

export const CustomerHealthSignalsTable = ({breakdown = [], initialSortKey, initialSortDir}) => {
    const theme = useTheme();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [sortKey, setSortKey] = useState(initialSortKey || 'total_risk_score');
    const [sortDir, setSortDir] = useState(initialSortDir || 'desc');

    // Re-applies whenever a new sort arrives from a chat "Show All ->" link -
    // the container doesn't remount this table on a query-param-only
    // navigation, so a mount-only initial value would miss it.
    useEffect(() => {
        if (initialSortKey) setSortKey(initialSortKey);
        if (initialSortDir) setSortDir(initialSortDir);
    }, [initialSortKey, initialSortDir]);

    const handleSort = (path) => {
        if (sortKey === path) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortKey(path);
            setSortDir('asc');
        }
        setPage(0);
    };

    const sortedRows = useMemo(() => sortRows(breakdown, sortKey, sortDir), [breakdown, sortKey, sortDir]);
    const paginatedRows = useMemo(
        () => sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sortedRows, page, rowsPerPage]
    );

    return (
        <Paper style={{...theme.palette.contentPanel, padding: 0}}>
            <TableContainer>
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        '& .MuiTableCell-head.MuiTableCell-stickyHeader': {
                            zIndex: 1,
                            backgroundColor: '#F5F7FB'
                        }
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {COLUMNS.map((column) => (
                                <ColumnHeaderCell
                                    key={column.key}
                                    column={column}
                                    sortKey={sortKey}
                                    sortDir={sortDir}
                                    onSort={handleSort}
                                />
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {breakdown.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={COLUMNS.length} style={{textAlign: 'center', padding: 30}}>
                                    <Typography color="textSecondary">No customers match the current filters</Typography>
                                </TableCell>
                            </TableRow>
                        ) : paginatedRows.map((row) => (
                            <TableRow key={row.id} hover>
                                {COLUMNS.map((column) => (
                                    <TableCell key={column.key} align={column.align} style={{width: column.width}}>
                                        {column.renderCell(row)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            {breakdown.length > 0 && (
                <TablePagination
                    component="div"
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    count={breakdown.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            )}
        </Paper>
    );
};
